import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code, Database, Cloud, Wrench, GitBranch, Star } from 'lucide-react';

const Skills: React.FC = () => {
  const [animateSkills, setAnimateSkills] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimateSkills(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('skills');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const skillCategories = [
    {
      icon: Code,
      title: 'Languages',
      skills: [
        { name: 'Java 17', level: 5 },
        { name: 'SQL', level: 5 },
        { name: 'PL/SQL', level: 4 },
        { name: 'JavaScript', level: 4 },
        { name: 'HTML', level: 3 }
      ]
    },
    {
      icon: GitBranch,
      title: 'Frameworks',
      skills: [
        { name: 'Spring Boot', level: 5 },
        { name: 'Spring Framework', level: 5 },
        { name: 'JPA', level: 4 },
        { name: 'REST APIs', level: 5 },
        { name: 'Microservices', level: 4 }
      ]
    },
    {
      icon: Database,
      title: 'Databases',
      skills: [
        { name: 'MySQL 8', level: 5 },
        { name: 'Oracle 11g/12c', level: 4 },
        { name: 'MongoDB', level: 3 },
        { name: 'Redis', level: 4 }
      ]
    },
    {
      icon: Cloud,
      title: 'Infrastructure',
      skills: [
        { name: 'Tomcat', level: 5 },
        { name: 'Nginx', level: 4 },
        { name: 'Kafka', level: 3 },
        { name: 'RabbitMQ', level: 4 },
        { name: 'ELK Stack', level: 3 }
      ]
    },
    {
      icon: Wrench,
      title: 'DevOps & Tools',
      skills: [
        { name: 'Git', level: 5 },
        { name: 'Maven', level: 5 },
        { name: 'Jira', level: 4 },
        { name: 'JMeter', level: 4 },
        { name: 'Swagger UI', level: 4 },
        { name: 'DataDog', level: 4 },
        { name: 'Eclipse', level: 5 },
        { name: 'IntelliJ IDEA', level: 5 }
      ]
    }
  ];

  const methodologies = [
    'Agile/Scrum Development',
    'CI/CD Implementation',
    'REST API Design',
    'Microservices Architecture',
    'System Design',
    'Performance Optimization',
    'Code Review & Quality Assurance',
    'Production Support & Monitoring'
  ];

  return (
    <section id="skills" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Technical <span className="gradient-text">Skills</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Comprehensive expertise across the full technology stack, from programming languages 
            to infrastructure and DevOps tools.
          </p>
        </motion.div>

        {/* Skill Categories */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
              viewport={{ once: true }}
              className="card p-6"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mr-4">
                  <category.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {category.title}
                </h3>
              </div>

              <div className="space-y-4">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skill.name}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {skill.name}
                      </span>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, starIndex) => (
                          <Star
                            key={starIndex}
                            size={14}
                            className={`${
                              starIndex < skill.level
                                ? 'text-yellow-500 fill-current'
                                : 'text-gray-300 dark:text-gray-600'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Methodologies */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="card p-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-8">
            Development Methodologies & Practices
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {methodologies.map((methodology, index) => (
              <motion.div
                key={methodology}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center p-3 bg-gray-50 dark:bg-dark-700 rounded-lg"
              >
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 flex-shrink-0"></div>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {methodology}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-8">
            Certifications
          </h3>
          <div className="card p-6 max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-4">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                <Code className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="text-left">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Oracle Certified Professional, Java SE 6 Programmer
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  Oracle Corporation • February 2013
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;
